/**
 * The one My Events / All Events preference, shared by the Pro home calendar
 * panel and both standalone calendar pages (CALENDAR_SEPARATION_PRD §5.7).
 *
 * Rules:
 * - An explicit click anywhere persists here and every surface honors it.
 * - With no stored choice, surfaces apply the smart default: users with
 *   nothing of "theirs" in view get All Events (a My Events default with
 *   nothing to show renders an empty calendar — the worst first impression);
 *   everyone else gets My Events. The default is derived from the fetched
 *   window (resolved O6 — no existence endpoint) and is never persisted:
 *   only a real click is a choice.
 * - The home panel's older per-card key seeds the shared one so an existing
 *   user's last panel choice survives the migration (§11: keys are additive,
 *   never renamed).
 */
import type { CalendarEvent } from '../types/calendar';

export type CalendarScope = 'my' | 'all';

const SHARED_KEY = 'cc-calendar-scope';
const LEGACY_HOME_KEY = 'cc-home-calendar-scope';

export function getStoredCalendarScope(): CalendarScope | null {
  try {
    const shared = window.localStorage.getItem(SHARED_KEY);
    if (shared === 'my' || shared === 'all') return shared;
    const legacy = window.localStorage.getItem(LEGACY_HOME_KEY);
    if (legacy === 'my' || legacy === 'all') return legacy;
    return null;
  } catch {
    return null; // private mode — smart default applies per render (§5.7)
  }
}

export function setStoredCalendarScope(scope: CalendarScope): void {
  try {
    window.localStorage.setItem(SHARED_KEY, scope);
  } catch {
    /* private mode */
  }
}

type MyishEvent = CalendarEvent & {
  is_tracked?: boolean;
  is_tracked_event?: boolean;
  tracked_bills?: unknown[];
};

/**
 * Smart default from an All Events window: 'my' when anything in it is the
 * user's — a directly tracked event, an event touching a tracked bill, or any
 * workspace event (workspace rows only ever come from the user's own
 * memberships — PRD §5.6).
 */
export function deriveDefaultScope(events: ReadonlyArray<CalendarEvent>): CalendarScope {
  const hasMine = events.some((event) => {
    const e = event as MyishEvent;
    return (
      e.is_tracked_event === true ||
      e.is_tracked === true ||
      (Array.isArray(e.tracked_bills) && e.tracked_bills.length > 0) ||
      e.event_source === 'workspace'
    );
  });
  return hasMine ? 'my' : 'all';
}
